/**
 * A screen above the person's access level is not rendered: the gate says
 * which level it needs and which the person has, and offers the way back.
 * The same level Sky answers within, read from the same place.
 */
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSession } from "../app/SessionContext";
import { canSee, LEVEL_LABEL, LEVEL_WHAT, screenLevel, useAccessLevel } from "../assistant/access";
import { principalKey } from "../assistant/preferences";
import { Card } from "../design/components";
import { Page } from "./AppShell";
import { t } from "../i18n";

export function AccessGate({ children }: { children: ReactNode }) {
  const session = useSession();
  const { pathname } = useLocation();
  const level = useAccessLevel(session.principal, principalKey(session.principal));
  if (canSee(level, pathname)) return <>{children}</>;
  const need = screenLevel(pathname);
  return (
    <Page eyebrow={t("{site} · Access", { site: session.site.code })} title="Above your access level">
      <Card
        title={t("This screen needs {level} access", { level: t(LEVEL_LABEL[need]) })}
        sub={t("your access on this copy is {level}", { level: t(LEVEL_LABEL[level]) })}
      >
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "var(--muted)", maxWidth: "80ch" }}>
          {t(
            "Nothing on it is shown, and Sky will not read or repeat what it holds. Your level covers {what}. Access levels are set by the platform with the sign-in; on this copy the level is set in",
            { what: t(LEVEL_WHAT[level]) },
          )}{" "}
          <Link to="/settings">{t("Settings")}</Link>.
        </p>
      </Card>
    </Page>
  );
}
